import { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { generateEnhancedClaims, EnhancedClaim, QuickFilter, applyQuickFilter } from '@/lib/claimsTrackerData';
import { ClaimIntelligenceDrawer } from '@/components/claims/ClaimIntelligenceDrawer';
import { ClaimCard } from '@/components/claims/ClaimCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Download, 
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  User,
  Clock,
  Send,
  XCircle,
  Activity,
  Shield,
  Zap,
  AlertTriangle,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  FileText,
  Eye,
  Filter,
} from 'lucide-react';

const STATUS_ICONS: Record<string, React.ReactNode> = {
  paid: <CheckCircle2 className="w-4 h-4 text-success" />,
  submitted: <Send className="w-4 h-4 text-primary" />,
  pending: <Clock className="w-4 h-4 text-warning" />,
  denied: <XCircle className="w-4 h-4 text-destructive" />,
  coding: <Activity className="w-4 h-4 text-chart-2" />,
  denial_check: <Shield className="w-4 h-4 text-chart-3" />,
  auto_fix: <Zap className="w-4 h-4 text-chart-4" />,
  note_signed: <FileText className="w-4 h-4 text-muted-foreground" />,
};

const QUICK_FILTERS: { value: QuickFilter; label: string; shortLabel: string; description: string }[] = [
  { value: 'all', label: 'All Claims', shortLabel: 'All', description: 'View all claims' },
  { value: 'high_risk', label: 'High Risk Denials', shortLabel: 'High Risk', description: 'Denial risk > 20%' },
  { value: 'needs_validation', label: 'Needs Validation', shortLabel: 'Validate', description: 'Confidence < 95%' },
  { value: 'high_value', label: 'High Value (>$5k)', shortLabel: '>$5k', description: 'Expected > $5,000' },
  { value: 'ready_to_transmit', label: 'Ready to Transmit', shortLabel: 'Ready', description: 'Approved for transmission' },
];

export default function ClaimsTracker() {
  const [allClaims] = useState<EnhancedClaim[]>(() => generateEnhancedClaims(25));
  const [selectedClaim, setSelectedClaim] = useState<EnhancedClaim | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = useIsMobile();

  // Apply filters
  const filteredClaims = useMemo(() => {
    let result = applyQuickFilter(allClaims, quickFilter);
    
    if (providerFilter !== 'all') {
      result = result.filter(c => c.provider === providerFilter);
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }

    // Apply global filter
    if (globalFilter) {
      const search = globalFilter.toLowerCase();
      result = result.filter(c => 
        c.id.toLowerCase().includes(search) ||
        c.patientInitials.toLowerCase().includes(search) ||
        c.provider.toLowerCase().includes(search)
      );
    }

    return result;
  }, [allClaims, quickFilter, providerFilter, statusFilter, globalFilter]);

  const columns = useMemo<ColumnDef<EnhancedClaim>[]>(() => [
    {
      id: 'status_icon',
      header: '',
      size: 40,
      cell: ({ row }) => {
        const status = row.original.status;
        const hasDrift = row.original.driftAlerts.length > 0;
        return (
          <div className="flex items-center justify-center relative">
            {STATUS_ICONS[status] || <Clock className="w-4 h-4 text-muted-foreground" />}
            {hasDrift && (
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-warning rounded-full animate-pulse" />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'id',
      header: 'Claim ID',
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-foreground">{getValue() as string}</span>
      ),
    },
    {
      id: 'patient',
      header: 'Patient ID',
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.patientInitials}-{row.original.patientDOBLast4}
        </span>
      ),
    },
    {
      id: 'service',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Service / Provider
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronsUpDown className="w-3 h-3 opacity-50" />
          )}
        </button>
      ),
      accessorFn: (row) => `${row.encounterDate.toISOString()}-${row.provider}`,
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <p className="text-xs text-muted-foreground">
            {format(row.original.encounterDate, 'MMM d, yyyy')}
          </p>
          <p className={cn(
            "text-sm font-medium",
            row.original.provider === 'Dr. Larkin' && "text-primary"
          )}>
            {row.original.provider}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'cptCodes',
      header: 'CPT Code',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.cptCodes.map((code) => (
            <Badge key={code} variant="secondary" className="font-mono text-[10px] bg-secondary/80">
              {code}
            </Badge>
          ))}
          {row.original.modifiers.map((mod) => (
            <Badge key={mod} variant="outline" className="font-mono text-[10px] text-chart-3">
              {mod}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'payer',
      header: 'Payer',
      cell: ({ getValue }) => (
        <span className="text-sm">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'expectedAmount',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors ml-auto"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Expected
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronsUpDown className="w-3 h-3 opacity-50" />
          )}
        </button>
      ),
      cell: ({ getValue }) => (
        <span className="font-mono text-sm text-right block">
          ${(getValue() as number).toLocaleString()}
        </span>
      ),
    },
    {
      accessorKey: 'confidenceScore',
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Confidence
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronsUpDown className="w-3 h-3 opacity-50" />
          )}
        </button>
      ),
      cell: ({ getValue, row }) => {
        const score = getValue() as number;
        const hasDrift = row.original.driftAlerts.length > 0;
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full rounded-full transition-all",
                  score >= 95 ? "bg-success" : score >= 90 ? "bg-warning" : "bg-destructive"
                )}
                style={{ width: `${score}%` }}
              />
            </div>
            <span className={cn(
              "font-mono text-xs font-medium",
              score >= 95 ? "text-success" : score >= 90 ? "text-warning" : "text-destructive"
            )}>
              {score.toFixed(1)}%
            </span>
            {hasDrift && <AlertTriangle className="w-3 h-3 text-warning" />}
          </div>
        );
      },
    },
    {
      id: 'action',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1 hover:bg-primary/10 hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedClaim(row.original);
            setDrawerOpen(true);
          }}
        >
          {row.original.confidenceScore < 95 ? (
            <>
              <User className="w-3 h-3" />
              Review
            </>
          ) : (
            <>
              <Eye className="w-3 h-3" />
              View
            </>
          )}
        </Button>
      ),
    },
  ], []);

  const table = useReactTable({
    data: filteredClaims,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  });

  const handleRowClick = (claim: EnhancedClaim) => {
    setSelectedClaim(claim);
    setDrawerOpen(true);
  };

  // Get unique providers for filter
  const providers = useMemo(() => 
    [...new Set(allClaims.map(c => c.provider))].sort(),
    [allClaims]
  );

  const hasActiveFilters = providerFilter !== 'all' || statusFilter !== 'all' || quickFilter !== 'all';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">Claims Tracker</h1>
          <p className="text-xs md:text-sm text-muted-foreground">
            {filteredClaims.length} claims · Operations-grade intelligence
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-2 self-start sm:self-auto min-h-[44px]">
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Export</span>
        </Button>
      </div>

      {/* Quick Filters - Scrollable on mobile */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
        {QUICK_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={quickFilter === filter.value ? 'default' : 'outline'}
            size="sm"
            className={cn(
              "text-xs h-9 min-h-[44px] whitespace-nowrap shrink-0",
              quickFilter === filter.value && "bg-primary text-primary-foreground"
            )}
            onClick={() => setQuickFilter(filter.value)}
          >
            <span className="md:hidden">{filter.shortLabel}</span>
            <span className="hidden md:inline">{filter.label}</span>
          </Button>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="glass-card p-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search claim ID or patient..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9 h-11 text-sm bg-secondary/50 border-border/50"
            />
          </div>
          
          {/* Mobile Filter Toggle */}
          <Button 
            variant="outline" 
            size="sm" 
            className="sm:hidden gap-2 h-11 min-h-[44px]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-1 bg-primary text-primary-foreground text-[10px] h-5">
                Active
              </Badge>
            )}
          </Button>
          
          {/* Desktop Filters */}
          <div className={cn(
            "flex-col sm:flex-row items-stretch sm:items-center gap-3",
            showFilters ? "flex" : "hidden sm:flex"
          )}>
            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="w-full sm:w-[140px] h-11 text-xs min-h-[44px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {providers.map(provider => (
                  <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[140px] h-11 text-xs min-h-[44px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="coding">Coding</SelectItem>
                <SelectItem value="denial_check">Denial Check</SelectItem>
                <SelectItem value="auto_fix">Auto-Fix</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-11 text-xs text-muted-foreground min-h-[44px]"
                onClick={() => {
                  setProviderFilter('all');
                  setStatusFilter('all');
                  setQuickFilter('all');
                  setGlobalFilter('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: Claim Cards */}
      {isMobile ? (
        <div className="space-y-3">
          {filteredClaims.map((claim) => (
            <ClaimCard 
              key={claim.id} 
              claim={claim} 
              onClick={() => handleRowClick(claim)} 
            />
          ))}
          {filteredClaims.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No claims match your filters.
            </div>
          )}
        </div>
      ) : (
        /* Desktop: Claims Table */
        <div className="glass-card overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-340px)] scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="border-b border-border hover:bg-transparent">
                    {headerGroup.headers.map((header) => (
                      <TableHead 
                        key={header.id}
                        className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2 px-3"
                        style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer border-b border-border/50 hover:bg-accent/30 transition-colors min-h-[48px]"
                      onClick={() => handleRowClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3 px-3">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                      No claims match your filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
            <p className="text-xs text-muted-foreground hidden sm:block">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{' '}
              of {table.getFilteredRowModel().rows.length} claims
            </p>
            <div className="flex items-center gap-2 mx-auto sm:mx-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="h-9 w-9 p-0 min-h-[44px] min-w-[44px]"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="h-9 w-9 p-0 min-h-[44px] min-w-[44px]"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <ClaimIntelligenceDrawer
        claim={selectedClaim}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}
